import * as React from "react";
import {
  PrimaryButton,
  Label,
  FontIcon,
  mergeStyleSets,
  mergeStyles,
  Separator,
  IChoiceGroupOption,
  ChoiceGroup,
  Fabric,
  Stack,
  IStackTokens
} from "office-ui-fabric-react";
import Progress from "./Progress";
import { useState, useEffect } from "react";

export interface AppProps {
  title: string;
  isOfficeInitialized: boolean;
}

export interface IAppSettings {
  SWConfirmMessage: string;
  SWMessageForwarded: string;
  SWMessageGoodJob: string;
  SWReportphishUrl: string;
  SWCustomHeader: string;
  SWEmailAction: string;
  SWButtonLabel: string;
  SWChoiceDelete: string;
  SWChoiceJunk: string;
  SWChoiceKeep: string;
  Email: string;
  DebugLog: boolean;
}

const iconClass = mergeStyles({
  fontSize: 50,
  height: 50,
  width: 50,
  margin: "0 25px"
});

const classNames = mergeStyleSets({
  red: [{ color: "red" }, iconClass],
  green: [{ color: "green" }, iconClass]
});

const itemAlignmentsStackTokens: IStackTokens = {
  childrenGap: 5,
  padding: 10
};

export const App: React.FC<AppProps> = (prop: AppProps) => {
  const [appSettings, setAppSettings] = useState<IAppSettings>(null);
  const [action, setAction] = useState("delete");
  const [mailState, setMailState] = useState({ isSWphish: false, isReported: false, swReportToken: "" });
  const [mailData, setMailData] = useState({ from: "", subject: "", body: "", bodyHtml: "", headers: "" });
  const [mailFile, setMailFile] = useState("");
  const { title, isOfficeInitialized } = prop;

  //Load settings and set mail change event
  useEffect(() => {
    writeLog("init data", false);
    fetchSettings();
    Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, _args => {
      writeLog("load new email data", false);
      setMailState({ isSWphish: false, isReported: false, swReportToken: "" });
      loadEmailData();
    });
  }, []);

  //load mail title and body then settings loaded
  useEffect(() => {
    writeLog("load email data", false);
    loadEmailData();
  }, [appSettings]);

  //check email data for phishing content
  useEffect(() => {
    writeLog("check email content", false);
    checkIFSWphish();
  }, [mailData]);

  //fetch all settings from json file
  const fetchSettings = async () => {
    const url = "./settings.json##SW_Versionning##";
    try {
      const response = await fetch(url);
      if (response.status === 200) {
        const data = await response.json();
        writeLog(data, false);
        setAppSettings(data);
      } else {
        writeLog(response.statusText, true);
      }
    } catch (error) {
      writeLog("catch fetchSettings", true);
      if (error) writeLog(error, true);
    }
  };

  //load email headers, subject and body
  const loadEmailData = () => {
    Office.context.mailbox.item.body.getAsync(Office.CoercionType.Text, result => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const tmpSubject = Office.context.mailbox.item.subject;
        const tmpFrom = Office.context.mailbox.item.from.emailAddress;
        const tmpBody = result.value;
        let tmpHeaders = "";
        Office.context.mailbox.item.getAllInternetHeadersAsync(function (asyncResult) {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) 
          {
            tmpHeaders = asyncResult.value;
          } else {
            writeLog("Error getting internet headers: " + JSON.stringify(asyncResult.error), true);
          }
        });
        Office.context.mailbox.item.body.getAsync(Office.CoercionType.Html, result => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            setMailData({ from: tmpFrom, subject: tmpSubject, body: tmpBody, bodyHtml: result.value, headers: tmpHeaders});
          } else {
            writeLog("Load body html Error: " + result.error, true);
          }
        });
      } else {
        writeLog("Load body Error: " + result.error, true);
      }
    });

    getEWSEmailAsFile();
  };

  //check if email is SW Phighing
  const checkIFSWphish = () => {
    let isSWphish = false;
    let swReportToken = "";
    //check for SWphish headers
    let myReg = new RegExp(appSettings?.SWCustomHeader + ".*", 'gim')
    if (mailData.headers.match(myReg)) 
    {
      isSWphish = true;
      swReportToken = mailData.headers.match(myReg)[0].slice(appSettings?.SWCustomHeader.length+2);
      writeLog("SW Phishing mail detected. Token: " + swReportToken, false);
    }
    setMailState({ isSWphish: isSWphish, isReported: false, swReportToken: swReportToken });
  };

  //Report action change
  const onChangeOption = (_ev: React.FormEvent<HTMLInputElement>, _option: IChoiceGroupOption) => {
    writeLog(_option.key, false);
    setAction(_option.key);
  };

  //send report to SW API
  const sendReportToSWphishApi = async () => {
    writeLog("", false);
    let reportUrl = appSettings?.SWReportphishUrl+mailState.swReportToken;
    writeLog("try send to: " + reportUrl, false);
    try {
      const response = await fetch(reportUrl, { method: "GET", mode: "no-cors"});
      if (response.status === 204) {
        const data = await response.text();
        writeLog("OK: " + data, false);
      } else {
        writeLog("Status: " + response.status + ": " + response.statusText, false);
      }
    } catch (error) {
      writeLog("catch sendReportToSWphishApi", false);
      if (error) writeLog(error, true);
      writeLog("try send (no-cors) to: " + reportUrl, false);
      try {
        const response = await fetch(reportUrl, { method: "GET", mode: "no-cors"});
        if (response.status === 204) {
          const data = await response.text();
          writeLog("OK: " + data, false);
        } else {
          writeLog("Status: " + response.status + ": " + response.statusText, false);
        }
      } catch (error) {
        writeLog("catch sendReportToSWphishApi", false);
        if (error) writeLog(error, true);
      }
    }
  };

  //create email as attachment for reporting it
  const getEWSEmailAsFile = async () => {
    writeLog("get EWS Email as file: ", false);
    var ewsId = Office.context.mailbox.item.itemId;
    var request =
    '<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages" xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">' +
    '  <soap:Header><t:RequestServerVersion Version="Exchange2013" /></soap:Header>' +
    "  <soap:Body>" +
    "    <m:GetItem>" +
    "      <m:ItemShape>" +
      // "        <t:BaseShape>Default</t:BaseShape>" +
    "        <t:BaseShape>IdOnly</t:BaseShape>' +" +
    "        <t:IncludeMimeContent>true</t:IncludeMimeContent>" +
      // "        <t:BodyType>Text</t:BodyType>" +
    "      </m:ItemShape >" +
    "      <m:ItemIds>" +
    '        <t:ItemId Id="' +
    ewsId +
    '" />' +
    "      </m:ItemIds>" +
    "    </m:GetItem>" +
    "  </soap:Body>" +
    "</soap:Envelope>";

    Office.context.mailbox.makeEwsRequestAsync(request, function(result) {
      writeLog("EWS result:" + result.status, false);
      var parser = new DOMParser();
      var doc = parser.parseFromString(result.value, "text/xml");
      var values = doc.getElementsByTagName("t:MimeContent");
      setMailFile(values[0].textContent);
    });
  };

  //send report email to security team
  const sendEmail = async () => {
    writeLog("send Email: ", false);
    Office.context.mailbox.getCallbackTokenAsync({ isRest: true }, async function(result) {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        var accessToken = result.value;
        writeLog("accessToken: OK", false);
        const api = Office.context.mailbox.restUrl + "/v2.0/me/sendmail";
        try {
          const param = {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken
            },
            body: JSON.stringify({
              Message: {
                Subject: "Email to analyze",
                Body: {
                  ContentType: "Text",
                  Content: ""
                },
                ToRecipients: [
                {
                  EmailAddress: {
                    Address: appSettings.Email
                  }
                }
                ],
                Attachments: [
                {
                  "@odata.type": "#Microsoft.OutlookServices.FileAttachment",
                  Name: "email.eml",
                  ContentBytes: mailFile
                }
                ]
              },
              SaveToSentItems: "false"
            })
          };
          const response = await fetch(api, param);
          writeLog("status" + response.status, false);
          if (response.status === 200 || response.status === 202) {
            const data = await response.text();
            writeLog(data, false);
          } else {
            writeLog("Error: " + response.statusText, true);
          }
        } catch (error) {
          writeLog("catch sendEmail", true);
          if (error) writeLog(error, true);
        }
      } else {
        writeLog("not access token", true);
      }
    });
  };

  //action when primary button clicked
  const sendReport = async () => {
    if (mailState.isSWphish) {
      sendReportToSWphishApi();
    } else {
      sendEmail();
    }
    let actionToDo = appSettings.SWEmailAction;
    if(appSettings.SWEmailAction == "askuser") {
      actionToDo = action;
    }
    switch (actionToDo) {
    case "delete":
      setTimeout(() => {
        moveToFolder("deleteditems");
      }, 3000);
      break;
    case "junk":
      if (mailState.isSWphish) {
        setTimeout(() => {
          moveToFolder("JunkEmail");
        }, 3000);
      } else {
        setTimeout(() => {
          moveToFolder("deleteditems");
        }, 3000);
      }
      break;
    }
    setMailState(prevState => ({ ...prevState, isReported: true }));
  };

  //move email to folder
  const moveToFolder = async (destinationFolder) => {
    writeLog("move To Folder: " + destinationFolder, false);
    console.log("move To Folder: " + destinationFolder);
    Office.context.mailbox.getCallbackTokenAsync({ isRest: true }, async function(result) {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        var accessToken = result.value;
        writeLog("accessToken: OK", false);
        const itemId = getItemRestId();
        const api = Office.context.mailbox.restUrl + "/v2.0/me/messages/" + itemId + "/move";
        const param = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + accessToken
          },
          body: JSON.stringify({
            DestinationId: destinationFolder
          })
        };
        writeLog(param, false);
        try {
          const response = await fetch(api, param);
          writeLog("status" + response.status, false);
          if (response.status === 200 || response.status === 201) {
            const data = await response.text();
            writeLog(data, false);
          } else {
            writeLog("Error: " + response.statusText, true);
          }
        } catch (error) {
          writeLog("catch moveEmails", true);
          if (error) writeLog(error, true);
        }
      } else {
        writeLog("not access token", true);
      }
    });
  };

  //get oultook item ID
  const getItemRestId = (): string => {
    if (Office.context.mailbox.diagnostics.hostName === "OutlookIOS") {
      // itemId is already REST-formatted.
      return Office.context.mailbox.item.itemId;
    } else {
      // Convert to an item ID for API v2.0.
      return Office.context.mailbox.convertToRestId(
        Office.context.mailbox.item.itemId,
        Office.MailboxEnums.RestVersion.v2_0
      );
    }
  };

  //retrieve header UI message
  const getHeader = (): any => {
    let ret = (
      <Fabric>
      <FontIcon iconName="MailTentative" className={classNames.red} />
      <Label>{appSettings.SWConfirmMessage}</Label>
      </Fabric>
      );
    if (mailState.isReported) {
      if (mailState.isSWphish) {
        ret = (
          <Fabric>
          <FontIcon iconName="Trophy" className={classNames.green} />
          <Label>{appSettings.SWMessageGoodJob}</Label>
          </Fabric>
          );
      } else {
        ret = (
          <Fabric>
          <FontIcon iconName="MailForward" className={classNames.green} />
          <Label>{appSettings.SWMessageForwarded}</Label>
          </Fabric>
          );
      }
    }
    return ret;
  };

  //retrieve action choice UI radio button and primary button
  const getChoicePanel = (): any => {
    if (mailState.isReported) {
      return <Fabric></Fabric>;
    }
    const options: IChoiceGroupOption[] = [
      { key: "delete", text: appSettings.SWChoiceDelete },
      { key: "junk", text: appSettings.SWChoiceJunk },
      { key: "keep", text: appSettings.SWChoiceKeep }
      ];
    return (
      <Fabric>
      <Stack tokens={itemAlignmentsStackTokens}>
      {appSettings.SWEmailAction == "askuser" ? (
        <Stack.Item align="auto">
        <ChoiceGroup
        defaultSelectedKey={action}
        options={options}
        onChange={onChangeOption}
        required={true}
        />
        </Stack.Item>
        ) : null}
      <Separator />
      <Stack.Item align="auto">
      <PrimaryButton
      iconProps={{ iconName: "ReportHacked" }}
      ariaDescription="{sendReport}"
      onClick={sendReport}
      style={{ width: "100%" }}
      >
      {appSettings.SWButtonLabel}
      </PrimaryButton>
      </Stack.Item>
      </Stack>
      </Fabric>
      );
  };

  //write log for debug and error purposes
  const writeLog = (text: any, errorlog: boolean) => {
    if (errorlog) {
      console.log(text);
    } else if (appSettings?.DebugLog) {
      console.log(text);
    }
  };

  //pre loader
  if (!isOfficeInitialized || appSettings === null) {
    return (
      <Progress title={title} logo="assets/icon-256.png" message="Please sideload your addin to see app body." />
      );
  }

  //main UI
  let messageIcon = getHeader();
  let choicePanel = getChoicePanel();
  return (
    <div>
    <div style={{ textAlign: "center" }}>{messageIcon}</div>
    {choicePanel}
    </div>
    );
};
